import React from 'react';
import moment from 'moment';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency, formatNumber } from '../../../../../../../CommonUtils/formatNumber';
import { PositionsDetailsLoading } from './PositionsDetails.loading';
import { PositionsDetailsError } from './PositionsDetails.error';
import { PositionsDetailsEmpty } from './PositionsDetails.empty';
import { usePositionsUnrealizedDetailsQuery } from '../../../../../../api/usePositionsUnrealizedDetailsQuery';
import { useRouteParams } from '../../../../../../hooks/useRouteParams';

interface PositionsDetailsProps {
  positionName: string;
}

export const PositionsDetails: React.FC<PositionsDetailsProps> = ({ positionName }) => {
  const { accountId, clientId, groupId } = useRouteParams();

  const { isFetching, isError, refetch, data } = usePositionsUnrealizedDetailsQuery({
    clientId,
    accountId,
    groupId,
    orionPositionName: positionName,
  });

  if (isFetching) {
    return <PositionsDetailsLoading />;
  }

  if (isError) {
    return <PositionsDetailsError onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return <PositionsDetailsEmpty />;
  }

  return (
    <tbody>
      {data.map((details, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={index}>
          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="left">
              {moment(details.purchaseDate).format('MM/DD/YYYY')}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {formatNumber(details.units, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {formatCurrency(details.price, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {formatCurrency(details.costPerShare, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {formatCurrency(details.marketValue, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {details.costBasis != null && formatCurrency(details.costBasis, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="right" variant="tabular">
              {details.unrealized != null && formatCurrency(details.unrealized, { decimalPlaces: 2 })}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} color="neutral.700" textAlign="left">
              {details.holdingPeriod}
            </Text>
          </td>
        </tr>
      ))}
    </tbody>
  );
};
